import { useNavigate } from "react-router-dom";
import helpStyle from "../styles/help.module.scss";

const HELP_SECTIONS = [
  {
    id: "section-1",
    title: "Navigating the App",
    paragraphs: [
      "MATLIB is designed to help you browse, compare, and save material references quickly. Use the main library view to scan available materials, apply filters, and open a material card for more detailed images, specifications, and availability information.",
      "When you open a material, focus on the details panel first. That view is where you can review finish, composition, color direction, imagery, and any supporting notes that help you decide whether the material is relevant to your project.",
      "If you are returning to the library after viewing an item, your best workflow is to keep filtering as you go. Narrowing by the most important properties first usually makes the library easier to scan than trying to review everything at once.",
    ],
  },
  {
    id: "section-2",
    title: "Building and Managing a Collection",
    paragraphs: [
      "Use the collection tools to save materials you want to revisit. A collection acts like a shortlist, making it easier to compare options across multiple vendors or categories without losing your place in the main library.",
      "The collection page supports both quick review and editing. Use Array view for visual scanning, then switch to List edit view when you need to reorder items or remove swatches before sharing.",
      "Changes to collection order are intentional and should be treated as presentation order for export workflows. Keep the highest-priority materials near the top if the output will be reviewed by a team.",
    ],
  },
  {
    id: "section-3",
    title: "Exporting a Collection to PDF",
    paragraphs: [
      "Use the dedicated Collection PDF page to prepare exports. Set a collection title, optional description, and decide whether pricing should be included before generating the document.",
      "In Array view, each swatch card can include multiple image options. Use the image selector dots to choose which image is sent to the PDF for each material.",
      "The page keeps a short session history of recently created PDFs so you can reopen previous exports quickly while working. This history is session-based and is not intended as long-term storage.",
    ],
  },
  {
    id: "section-4",
    title: "Sharing and Reviewing Materials",
    paragraphs: [
      "When sharing materials internally, use the material detail view and collection tools to gather the clearest images and most relevant specifications first. This reduces back-and-forth and helps reviewers understand why each option was selected.",
      "If your workflow includes exporting or presenting materials externally, confirm that product names, imagery, and notes are current before sharing. MATLIB should support review and selection, but final purchasing or production decisions should still be checked against the most recent vendor information.",
    ],
  },
  {
    id: "section-5",
    title: "Account Access and Support",
    paragraphs: [
      "Access to MATLIB may differ by user type. Some users may have a direct login, while others may access the library through an approved identifier or vendor-specific workflow. If your access stops working, contact the MATLIB administrator or your internal point of contact.",
      "Use the Contact page when you need platform support, sourcing guidance, or help with collection workflows. Include a clear subject and enough context in your message so the team can respond faster.",
      "If you are building a collection for a team discussion, make sure the materials you keep in that list are the ones you would realistically present. A tighter collection usually makes decision-making faster.",
      "For the best experience, use current browser versions and avoid having multiple sessions open in different tabs when editing or reviewing materials. If something looks outdated, refresh the page and reopen the material before assuming the data is current.",
    ],
  },
];

const PRIVACY_SECTIONS = [
  {
    title: "Generic Privacy Notice",
    paragraphs: [
      "MATLIB may collect limited account, usage, and device information needed to operate the application, maintain security, and improve the user experience. This can include login identifiers, session data, pages viewed, and actions taken within the library.",
      "Information made available through MATLIB is intended for authorized business use. Access may be logged for security, troubleshooting, service quality, and administrative review.",
      "MATLIB does not intentionally request more personal information than is reasonably needed to provide access to the service. Users should avoid submitting sensitive personal data through notes, filenames, or other freeform fields unless expressly required by the organization operating the platform.",
    ],
  },
  {
    title: "How Information May Be Used",
    paragraphs: [
      "Collected information may be used to authenticate users, support saved collections, troubleshoot technical issues, analyze product usage, and maintain the security and reliability of the service.",
      "Information may also be shared with service providers, hosting providers, or authorized administrators where reasonably necessary to operate, secure, maintain, or support the platform.",
    ],
  },
  {
    title: "Retention and Responsibility",
    paragraphs: [
      "Information may be retained for as long as necessary to operate the service, meet internal business requirements, resolve disputes, enforce policies, or comply with applicable legal obligations.",
      "This privacy section is a general informational clause only and is not legal advice. If MATLIB is used commercially or by external users, the business operating it should have counsel review and approve a formal privacy policy tailored to its actual data practices and legal requirements.",
    ],
  },
];

const HELP_NAV = [
  { id: "section-1", label: "Navigation" },
  { id: "section-2", label: "Collections" },
  { id: "section-3", label: "PDF Export" },
  { id: "section-4", label: "Sharing" },
  { id: "section-5", label: "Support" },
  { id: "privacy", label: "Privacy" },
];

function Help() {
  const navigate = useNavigate();

  const handleClickScroll = (section) => {
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <div className={`content ${helpStyle.helpPage}`}>
      <div className={helpStyle.helpBackdrop} aria-hidden="true"></div>

      <div className={helpStyle.helpLayout}>
        <aside className={helpStyle.helpSidebar}>
          <div className={helpStyle.sidebarCard}>
            <p className={helpStyle.sidebarEyebrow}>Support</p>
            <h1 className={helpStyle.sidebarTitle}>MATLIB Help</h1>
            <p className={helpStyle.sidebarCopy}>
              Guidance for browsing materials, managing collections, and
              understanding general privacy expectations.
            </p>

            <div className={helpStyle.sidebarNav}>
              {HELP_NAV.map((item) => (
                <button
                  key={item.id}
                  className={helpStyle.navButton}
                  onClick={() => handleClickScroll(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <button
              className={helpStyle.backButton}
              onClick={() => navigate("/library")}
            >
              Back to the Library
            </button>
          </div>
        </aside>

        <main className={helpStyle.helpBody}>
          <section className={helpStyle.heroPanel}>
            <div>
              <p className={helpStyle.sectionEyebrow}>Quick Start</p>
              <h2 className={helpStyle.heroTitle}>
                Clean guidance for using MATLIB effectively.
              </h2>
              <p className={helpStyle.heroCopy}>
                Start with navigation, build a focused collection, and use the
                privacy section as a general reference for account and usage
                expectations.
              </p>
            </div>

            <div className={helpStyle.heroStats}>
              <div className={helpStyle.statCard}>
                <span>5</span>
                <p>Core help sections</p>
              </div>
              <div className={helpStyle.statCard}>
                <span>1</span>
                <p>Privacy overview</p>
              </div>
            </div>
          </section>

          {HELP_SECTIONS.map((section) => (
            <section
              key={section.id}
              id={section.id}
              className={helpStyle.helpSection}
            >
              <p className={helpStyle.sectionEyebrow}>Help Section</p>
              <h2 className={helpStyle.sectionTitle}>{section.title}</h2>
              <div className={helpStyle.sectionDivider}></div>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph} className={helpStyle.sectionParagraph}>
                  {paragraph}
                </p>
              ))}
            </section>
          ))}

          <div id="privacy" className={helpStyle.anchorOffset}></div>
          <div className={helpStyle.privacyHeader}>
            <p className={helpStyle.sectionEyebrow}>Policy</p>
            <h1 className={helpStyle.privacyTitle}>Privacy Policy</h1>
          </div>
          {PRIVACY_SECTIONS.map((section) => (
            <section key={section.title} className={helpStyle.helpSection}>
              <h2 className={helpStyle.sectionTitle}>{section.title}</h2>
              <div className={helpStyle.sectionDivider}></div>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph} className={helpStyle.sectionParagraph}>
                  {paragraph}
                </p>
              ))}
            </section>
          ))}
        </main>
      </div>
    </div>
  );
}

export default Help;
